import React, { useMemo, useState } from "react";
import { buildMarketCapTierBreakdown } from "../../shared/utils/stockMarketCapTier";
import { buildStockSectorBreakdown } from "../../shared/utils/stockSectorBreakdown";
import {
  buildStockEquityConcentrationBreakdown,
  isStockEquityDrillable,
} from "../../shared/utils/stockEquityConcentration";
import { formatCurrencyUsd } from "../../shared/utils/financeDisplay";
import { formatStockPercentShare } from "../../shared/utils/stockPercentFormat";
import { sortRowsByConfig } from "../../shared/utils/sortRowsByConfig";
import { useTableSort } from "../../shared/hooks/useTableSort";
import { SortableTableTh } from "../TableShell";

const VIEW_MARKET_CAP = "marketCap";
const VIEW_SECTOR = "sector";
const VIEW_EQUITY = "equity";

const PIE_SIZE = 280;
const PIE_CX = PIE_SIZE / 2;
const PIE_CY = PIE_SIZE / 2;
const PIE_R = PIE_SIZE / 2;

const PIE_COLORS = ["#0d6efd", "#198754", "#ffc107", "#dc3545", "#6f42c1", "#fd7e14", "#20c997", "#6610f2"];

const ANALYSIS_SORT_KEY_TYPES = {
  label: "string",
  rangeLabel: "string",
  count: "number",
  investmentPct: "number",
  investment: "number",
};

const DEFAULT_ANALYSIS_SORT = { key: "investment", direction: "desc" };

/** Shared Analysis table column labels — keep identical across Market cap, Sector, and Investment views. */
const STOCK_ANALYSIS_COLUMN_LABELS = {
  stocks: "Stocks",
  investmentPct: "Investment (%)",
  investmentUsd: "Investment ($)",
};

function polarToCartesian(cx, cy, radius, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
}

function describePieSlice(cx, cy, radius, startAngle, endAngle) {
  if (endAngle - startAngle >= 360) {
    return `M ${cx - radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx + radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx - radius} ${cy} Z`;
  }
  const start = polarToCartesian(cx, cy, radius, startAngle);
  const end = polarToCartesian(cx, cy, radius, endAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}

function segmentColor(segment, segmentsWithData, fallbackIndex) {
  if (segment.count <= 0 && segment.investment <= 0) return "#e9ecef";
  const idx = segmentsWithData.findIndex((x) => x.id === segment.id);
  return PIE_COLORS[(idx >= 0 ? idx : fallbackIndex) % PIE_COLORS.length];
}

function DistributionPieChart({ segmentsWithData, onSegmentDrill, getSlicePct, isDrillable }) {
  let angle = 0;

  return (
    <svg
      width={PIE_SIZE}
      height={PIE_SIZE}
      role="img"
      aria-label={segmentsWithData.map((s) => `${s.label}: ${formatStockPercentShare(getSlicePct(s))}`).join("; ")}
    >
      {segmentsWithData.map((s, i) => {
        const slicePct = getSlicePct(s);
        const sweep = (slicePct / 100) * 360;
        const startAngle = angle;
        const endAngle = angle + sweep;
        angle = endAngle;
        const color = PIE_COLORS[i % PIE_COLORS.length];
        const clickable = onSegmentDrill && isDrillable(s) && slicePct > 0;

        return (
          <path
            key={s.id}
            d={describePieSlice(PIE_CX, PIE_CY, PIE_R, startAngle, endAngle)}
            fill={color}
            stroke="#fff"
            strokeWidth={1}
            style={{ cursor: clickable ? "pointer" : "default" }}
            onClick={() => clickable && onSegmentDrill(s.id)}
            onKeyDown={(e) => {
              if (clickable && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onSegmentDrill(s.id);
              }
            }}
            tabIndex={clickable ? 0 : -1}
            role={clickable ? "button" : undefined}
            aria-label={clickable ? `View ${s.label} in list` : undefined}
          >
            <title>{`${s.label}: ${formatCurrencyUsd(s.investment)} (${formatStockPercentShare(slicePct)} of equity)`}</title>
          </path>
        );
      })}
    </svg>
  );
}

const VIEW_CONFIG = {
  [VIEW_MARKET_CAP]: {
    title: "Market cap distribution",
    hint:
      "Holdings grouped by market capitalization tier. % is share of total equity; count is number of stocks per tier.",
    categoryColumnLabel: "Category",
    detailColumnLabel: "Market cap range",
    countColumnLabel: STOCK_ANALYSIS_COLUMN_LABELS.stocks,
    pctColumnLabel: STOCK_ANALYSIS_COLUMN_LABELS.investmentPct,
    showCountColumn: true,
    showDetailColumn: true,
    getSlicePct: (s) => s.investmentPct,
    segmentHasChartData: (s) => s.investment > 0,
    isDrillable: () => true,
    emptyMessage: "No stocks with market cap data to chart.",
    missingMessage: (n) =>
      `${n} stock${n === 1 ? "" : "s"} without market cap ${n === 1 ? "is" : "are"} excluded from this chart.`,
  },
  [VIEW_SECTOR]: {
    title: "Sector distribution",
    hint: "Holdings grouped by sector. % is share of total equity; count is number of stocks per sector.",
    categoryColumnLabel: "Category",
    detailColumnLabel: null,
    countColumnLabel: STOCK_ANALYSIS_COLUMN_LABELS.stocks,
    pctColumnLabel: STOCK_ANALYSIS_COLUMN_LABELS.investmentPct,
    showCountColumn: true,
    showDetailColumn: false,
    getSlicePct: (s) => s.investmentPct,
    segmentHasChartData: (s) => s.investment > 0,
    isDrillable: () => true,
    emptyMessage: "No stocks to chart.",
    missingMessage: null,
  },
  [VIEW_EQUITY]: {
    title: "Equity concentration",
    hint:
      "Holdings ranked by equity (top 10 by investment). Slice size is share of total equity—use this to spot concentration in a few stocks.",
    categoryColumnLabel: "Stock",
    detailColumnLabel: "Name",
    countColumnLabel: STOCK_ANALYSIS_COLUMN_LABELS.stocks,
    pctColumnLabel: STOCK_ANALYSIS_COLUMN_LABELS.investmentPct,
    showCountColumn: true,
    showDetailColumn: true,
    getSlicePct: (s) => s.investmentPct,
    segmentHasChartData: (s) => s.investment > 0,
    isDrillable: (s) => isStockEquityDrillable(s.id),
    emptyMessage: "No stocks with equity to chart.",
    missingMessage: (n) =>
      `${n} stock${n === 1 ? "" : "s"} without equity ${n === 1 ? "is" : "are"} excluded from this chart.`,
  },
};

export default function StockHoldingsAnalysis({ stocks, onMarketCapDrill, onSectorDrill, onEquityDrill }) {
  const [viewBy, setViewBy] = useState(VIEW_MARKET_CAP);
  const { sortConfig, handleSort } = useTableSort(DEFAULT_ANALYSIS_SORT, viewBy);

  const marketCapBreakdown = useMemo(() => buildMarketCapTierBreakdown(stocks), [stocks]);
  const sectorBreakdown = useMemo(() => buildStockSectorBreakdown(stocks), [stocks]);
  const equityBreakdown = useMemo(() => buildStockEquityConcentrationBreakdown(stocks), [stocks]);

  const breakdown =
    viewBy === VIEW_SECTOR ? sectorBreakdown : viewBy === VIEW_EQUITY ? equityBreakdown : marketCapBreakdown;
  const { segments, total, missing } = breakdown;
  const config = VIEW_CONFIG[viewBy];
  const sortedSegments = useMemo(
    () => sortRowsByConfig(segments, sortConfig, ANALYSIS_SORT_KEY_TYPES),
    [segments, sortConfig]
  );
  const segmentsWithData = segments.filter(config.segmentHasChartData);
  const onSegmentDrill =
    viewBy === VIEW_SECTOR ? onSectorDrill : viewBy === VIEW_EQUITY ? onEquityDrill : onMarketCapDrill;
  const drillHint = onSegmentDrill ? " Click a slice or row to view those stocks in the list." : "";

  return (
    <div className="panel page-section" style={{ padding: "24px" }}>
      <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#495057", whiteSpace: "nowrap" }}>View by:</span>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
            <input
              type="radio"
              name="stockAnalysisViewBy"
              value={VIEW_MARKET_CAP}
              checked={viewBy === VIEW_MARKET_CAP}
              onChange={() => setViewBy(VIEW_MARKET_CAP)}
            />
            Market cap
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
            <input
              type="radio"
              name="stockAnalysisViewBy"
              value={VIEW_SECTOR}
              checked={viewBy === VIEW_SECTOR}
              onChange={() => setViewBy(VIEW_SECTOR)}
            />
            Sector
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
            <input
              type="radio"
              name="stockAnalysisViewBy"
              value={VIEW_EQUITY}
              checked={viewBy === VIEW_EQUITY}
              onChange={() => setViewBy(VIEW_EQUITY)}
            />
            Investment
          </label>
        </span>
      </div>

      <div className="panel-title" style={{ marginBottom: 4 }}>
        {config.title}
      </div>
      <p className="panel-hint" style={{ marginTop: 0, marginBottom: 16 }}>
        {config.hint}
        {drillHint}
      </p>

      {total === 0 ? (
        <p className="panel-hint">{config.emptyMessage}</p>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "32px" }}>
          <div style={{ flexShrink: 0 }}>
            <DistributionPieChart
              segmentsWithData={segmentsWithData}
              onSegmentDrill={onSegmentDrill}
              getSlicePct={config.getSlicePct}
              isDrillable={config.isDrillable}
            />
          </div>
          <div style={{ flex: "1", minWidth: "240px" }}>
            <div className="table-wrapper" style={{ display: "inline-block", maxWidth: "100%" }}>
              <table style={{ width: "100%", minWidth: config.showDetailColumn ? "720px" : "640px" }}>
                <thead>
                  <tr>
                    <SortableTableTh
                      sortKey="label"
                      currentSortKey={sortConfig.key}
                      direction={sortConfig.direction}
                      onSort={handleSort}
                      className="table-th-nowrap"
                    >
                      {config.categoryColumnLabel}
                    </SortableTableTh>
                    {config.showDetailColumn && config.detailColumnLabel ? (
                      <SortableTableTh
                        sortKey="rangeLabel"
                        currentSortKey={sortConfig.key}
                        direction={sortConfig.direction}
                        onSort={handleSort}
                        className="table-th-nowrap"
                      >
                        {config.detailColumnLabel}
                      </SortableTableTh>
                    ) : null}
                    {config.showCountColumn ? (
                      <SortableTableTh
                        sortKey="count"
                        currentSortKey={sortConfig.key}
                        direction={sortConfig.direction}
                        onSort={handleSort}
                        className="table-cell-numeric"
                      >
                        {config.countColumnLabel}
                      </SortableTableTh>
                    ) : null}
                    <SortableTableTh
                      sortKey="investmentPct"
                      currentSortKey={sortConfig.key}
                      direction={sortConfig.direction}
                      onSort={handleSort}
                      className="table-cell-numeric"
                    >
                      {config.pctColumnLabel}
                    </SortableTableTh>
                    <SortableTableTh
                      sortKey="investment"
                      currentSortKey={sortConfig.key}
                      direction={sortConfig.direction}
                      onSort={handleSort}
                      className="table-cell-numeric"
                    >
                      {STOCK_ANALYSIS_COLUMN_LABELS.investmentUsd}
                    </SortableTableTh>
                  </tr>
                </thead>
                <tbody>
                  {sortedSegments.map((s, i) => {
                    const clickable = onSegmentDrill && config.isDrillable(s) && config.segmentHasChartData(s);
                    const displayPct = config.getSlicePct(s);
                    return (
                      <tr
                        key={s.id}
                        onClick={() => clickable && onSegmentDrill(s.id)}
                        onKeyDown={(e) => {
                          if (clickable && (e.key === "Enter" || e.key === " ")) {
                            e.preventDefault();
                            onSegmentDrill(s.id);
                          }
                        }}
                        tabIndex={clickable ? 0 : undefined}
                        role={clickable ? "button" : undefined}
                        style={{ cursor: clickable ? "pointer" : "default" }}
                        title={clickable ? `View in list` : undefined}
                      >
                        <td>
                          <span
                            className="badge"
                            style={{
                              width: 12,
                              height: 12,
                              padding: 0,
                              marginRight: 8,
                              borderRadius: 2,
                              backgroundColor: segmentColor(s, segmentsWithData, i),
                              verticalAlign: "middle",
                            }}
                            aria-hidden
                          />
                          {s.label}
                        </td>
                        {config.showDetailColumn && config.detailColumnLabel ? (
                          <td style={{ color: "#6c757d", fontSize: "13px" }}>{s.rangeLabel}</td>
                        ) : null}
                        {config.showCountColumn ? (
                          <td className="table-cell-numeric">{s.count}</td>
                        ) : null}
                        <td className="table-cell-numeric">{formatStockPercentShare(displayPct)}</td>
                        <td className="table-cell-numeric">
                          {s.investment > 0 ? formatCurrencyUsd(s.investment) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {missing > 0 && config.missingMessage ? (
        <p className="panel-hint" style={{ marginTop: 16, marginBottom: 0 }}>
          {config.missingMessage(missing)}
        </p>
      ) : null}
    </div>
  );
}
